import './Row.css'
import React from 'react'
import { connect } from 'react-redux'

import * as NotificationsTypes from 'remote/notifications/types'

import {
  selectors as NotificationsSelectors,
  actions as NotificationsActions,
} from 'data/notifications'
import { trackNoteClicked } from 'data/analytics/actions'
import { selectors as UsersSelectors } from 'data/users'
import { selectors as ChannelsSelectors } from 'data/channels'
import { selectors as NotesSelectors } from 'data/notes'
import NotificationRowLayout from './NotificationRowLayout'
import UserMention from 'components/Utils/UserMention'

import { getNotificationNavigate } from 'data/notifications/settings'

export function NotificationRow({
  notification,
  metadataEntities,
  style,
  className,
  navigate,
  markAsRead,
  markAsUnread,
  trackNoteClicked,
  onClick = () => {},
}) {
  const isNote = Boolean(notification.metadata.get('noteTitle'))
  const onRowClick = React.useCallback(
    event => {
      if (isNote) {
        trackNoteClicked({ source: 'notification' })
      }
      onClick(event)
      navigate(notification)
    },
    [onClick, trackNoteClicked, navigate, notification, isNote]
  )

  //Condition on ContentTypeMap blocks notification type sent by the API but unknown by the APP.
  //It prevents the notification dropdown from breaking and the user can keep accessing other notifications
  //recognized by the app (e.g mapped in the ContentTypeMap).
  if (!notification || !ContentTypeMap[notification.type]) {
    return null
  }

  const content = ContentTypeMap[notification.type]({
    notification,
    metadataEntities,
  })

  const { author } = metadataEntities

  const avatarProps = {
    url: author && author.photoURL,
    name:
      (author && author.username) || notification.metadata.get('authorName'),
  }

  return (
    <NotificationRowLayout
      avatarProps={avatarProps}
      content={content}
      timeago={notification.createdAt}
      aknowledged={!!notification.aknowledgedAt}
      style={style}
      className={className}
      onClick={onRowClick}
      markAsRead={
        notification.aknowledgedAt ? () => {} : () => markAsRead(notification)
      }
      markAsUnread={
        !notification.aknowledgedAt
          ? () => {}
          : () => markAsUnread(notification)
      }
    />
  )
}

export default connect(
  (state, { notificationId, notificationIndex }) => {
    const notification =
      notificationIndex != null
        ? NotificationsSelectors.getNotificationFromIndex(
            state,
            notificationIndex
          )
        : NotificationsSelectors.getNotification(state, notificationId)

    if (!notification) {
      return {}
    }

    const { metadata } = notification

    const author =
      metadata.get('authorId') &&
      UsersSelectors.getUser(state, metadata.get('authorId'))
    const channel =
      metadata.get('channelId') &&
      ChannelsSelectors.getChannel(state, metadata.get('channelId'))
    const note =
      metadata.get('noteId') &&
      NotesSelectors.getNote(state, metadata.get('noteId'))
    return {
      notification,
      metadataEntities: {
        author,
        channel,
        note,
      },
    }
  },
  (dispatch, { closePopOver }) => ({
    trackNoteClicked,
    markAsRead(notification) {
      dispatch(
        NotificationsActions.acknowledgeNotifications(
          ({ id }) => id === notification.id
        )
      )
    },
    markAsUnread(notification) {
      dispatch(
        NotificationsActions.unAcknowledgeNotifications(
          ({ id }) => id === notification.id
        )
      )
    },
    navigate(notification) {
      if (
        [
          NotificationsTypes.MENTION_IN_COMMENT,
          NotificationsTypes.MENTION_IN_NOTE,
          NotificationsTypes.ADD_THREAD_TO_NOTE,
          NotificationsTypes.REPLY_TO_COMMENT,
          NotificationsTypes.NUDGE_ABOUT_NOTE,
        ].includes(notification.type)
      ) {
        // Since opening the comment panel activates the focus mode, we need to close the notifications popover in that case
        if (closePopOver) {
          closePopOver()
        }
      }
      getNotificationNavigate(notification.type)(dispatch, notification)
    },
  })
)(NotificationRow)

export function RegularContent({ authorName, destinationName, text }) {
  return (
    <span className="shade-10">
      <UserMention username={authorName} />
      <span> {text} </span>
      <span className="font-weight-medium shade-05">{destinationName}</span>
    </span>
  )
}

export function NoteAccessContent({ authorName, noteName, channelName, text }) {
  return (
    <span className="shade-10">
      <UserMention username={authorName} />
      <span>{' would like to access '}</span>
      <span className="font-weight-medium shade-05">{noteName}</span>
      <span>{' in '}</span>
      <span className="font-weight-medium shade-05">{channelName}</span>
      <span>{'. Add '}</span>
      <UserMention username={authorName} />
      <span>{' to '}</span>
      <span className="font-weight-medium shade-05">{channelName}</span>
      <span>{' or move '}</span>
      <span className="font-weight-medium shade-05">{noteName}</span>
      <span>{'.'}</span>
    </span>
  )
}

export const ContentTypeMap = {
  [NotificationsTypes.ADD_USER_TO_CHANNEL]({ notification, metadataEntities }) {
    const { author, channel } = metadataEntities
    return (
      <RegularContent
        authorName={
          (author && author.username) || notification.metadata.get('authorName')
        }
        text="added you to the channel"
        destinationName={
          (channel && channel.name) || notification.metadata.get('channelName')
        }
      />
    )
  },
  [NotificationsTypes.UPDATE_USER_ROLES_ON_CHANNEL]({
    notification,
    metadataEntities,
  }) {
    const { author, channel } = metadataEntities
    return (
      <RegularContent
        authorName={
          (author && author.username) || notification.metadata.get('authorName')
        }
        text={` updated your role to ${notification.metadata.get(
          'role'
        )} on the channel `}
        destinationName={
          (channel && channel.name) || notification.metadata.get('channelName')
        }
      />
    )
  },
  [NotificationsTypes.ADD_THREAD_TO_NOTE]({ notification, metadataEntities }) {
    const { author, note } = metadataEntities
    return (
      <RegularContent
        authorName={
          (author && author.username) || notification.metadata.get('authorName')
        }
        text="wrote a comment on"
        destinationName={
          (note && note.title) || notification.metadata.get('noteTitle')
        }
      />
    )
  },
  [NotificationsTypes.MENTION_IN_NOTE]({ notification, metadataEntities }) {
    const { author, note } = metadataEntities
    return author ? (
      <RegularContent
        authorName={author.username || notification.metadata.get('authorName')}
        text="mentioned you in the doc"
        destinationName={
          (note && note.title) || notification.metadata.get('noteTitle')
        }
      />
    ) : (
      <span className="shade-10">
        <span>You've been mentioned in the doc </span>
        <span className="font-weight-medium shade-05">
          {(note && note.title) || notification.metadata.get('noteTitle')}
        </span>
      </span>
    )
  },
  [NotificationsTypes.MENTION_IN_COMMENT]({ notification, metadataEntities }) {
    const { author, note } = metadataEntities
    return (
      <RegularContent
        authorName={
          (author && author.username) || notification.metadata.get('authorName')
        }
        text="mentioned you in a comment in"
        destinationName={
          (note && note.title) || notification.metadata.get('noteTitle')
        }
      />
    )
  },
  [NotificationsTypes.REPLY_TO_COMMENT]({ notification, metadataEntities }) {
    const { author, note } = metadataEntities
    return (
      <RegularContent
        authorName={
          (author && author.username) || notification.metadata.get('authorName')
        }
        text="replied to your comment in"
        destinationName={
          (note && note.title) || notification.metadata.get('noteTitle')
        }
      />
    )
  },
  [NotificationsTypes.NUDGE_ABOUT_NOTE]({ notification, metadataEntities }) {
    const { author, note } = metadataEntities
    return (
      <RegularContent
        authorName={
          (author && author.username) || notification.metadata.get('authorName')
        }
        text="pinged you on the doc"
        destinationName={
          (note && note.title) || notification.metadata.get('noteTitle')
        }
      />
    )
  },
  [NotificationsTypes.REQUEST_CHANNEL_ACCESS]({
    notification,
    metadataEntities,
  }) {
    const { author, channel } = metadataEntities
    return (
      <RegularContent
        authorName={
          (author && author.username) || notification.metadata.get('authorName')
        }
        text="would like to access channel"
        destinationName={
          (channel && channel.name) || notification.metadata.get('channelName')
        }
      />
    )
  },
  [NotificationsTypes.REQUEST_NOTE_ACCESS]({ notification, metadataEntities }) {
    const { author, note, channel } = metadataEntities
    return (
      <NoteAccessContent
        authorName={
          (author && author.username) || notification.metadata.get('authorName')
        }
        noteName={
          (note && note.title) || notification.metadata.get('noteTitle')
        }
        channelName={
          (channel && channel.name) || notification.metadata.get('channelName')
        }
      />
    )
  },
}
